import { Box } from '@mui/material';
import { AxiosError } from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthProvider, useNotify } from 'react-admin';
import { Identity } from '../../../interfaces/auth';
import { RpmPatientSelectionFilters } from '../../../interfaces/billing';
import { BillingMember, BillingPagination, BillingProcess } from '../../../interfaces/billing.rpm';
import { IClinic } from '../../../interfaces/clinic';
import { SelectOption } from '../../../interfaces/common';
import { PayerItem } from '../../../interfaces/eligibility';
import { ConditionStatus } from '../../../interfaces/health-data';
import { BillingService } from '../../../services';
import coreService from '../../../services/core';
import eligibilityService from '../../../services/eligibility';
import healthDataService from '../../../services/health-data';
import { INVALID_STORAGE_ERROR } from '../common/constants';
import { RPMFilters } from '../common/RPMFilters';
import { ActionableTab } from '../common/types';
import { isActiveStatus } from '../common/ProcessStatusIndicator';
import {
  FilterConfiguration,
  Filters,
  INITIAL_PAGINATION,
  LoadingStates,
  RPMCptCodes,
} from '../types/rpms.types';
import { PatientSelectionTable } from './PatientSelectionTable';

type PatientSelectionProps = ActionableTab & { 
  clinics: IClinic[];
  currentProcess?: BillingProcess | null;
};

const filterConfigurations = [
  {
    key: 'clinic',
    label: 'Clinic',
    options: [],
    section: 'Patient Selection',
    type: 'select',
  },
  {
    key: 'optOutStatus',
    label: 'Opt Out Status',
    options: [
      { value: 'disabled', label: 'Disabled' },
      { value: 'enabled', label: 'Enabled' },
    ],
    section: 'Patient Selection',
    type: 'select',
  },
  {
    key: 'icd10Codes',
    label: 'ICD10 Codes',
    options: [],
    section: 'Patient Selection',
    type: 'select',
  },
  {
    key: 'insuranceCompany',
    label: 'Insurance Company',
    options: [],
    section: 'Patient Selection',
    type: 'autocomplete',
    fetchOptions: async (searchTerm: string) => {
      try {
        const response = await eligibilityService.getPayers({
          filter: searchTerm || null,
        });
        const options =
          response?.data?.map((payer: PayerItem) => ({
            label: payer.name,
            value: payer.id,
          })) || [];
        return options;
      } catch (error) {
        console.error('Error fetching insurance companies:', error);
        return [];
      }
    },
  },
  {
    key: 'cptCodes',
    label: 'Available CPT Codes',
    options: Object.values(RPMCptCodes).map((value) => ({ value, label: value })),
    section: 'Patient Selection',
    type: 'select',
  },
  {
    key: 'rpmStatus',
    label: 'RPM Status',
    options: [
      { value: 'valid', label: 'Valid' },
      { value: 'invalid', label: 'Invalid' },
    ],
    section: 'Patient Selection',
    type: 'select',
  },
  {
    key: 'memberSearch',
    label: 'Member Search',
    options: [],
    section: 'Patient Selection',
    type: 'autocomplete',
    fetchOptions: async (searchTerm: string) => {
      try {
        if (!searchTerm) {
          return [];
        }

        const response = await coreService.fetchMembers({
          searchTerm,
        });
        const options =
          response?.data?.map((member) => ({
            label: member.fullName,
            value: member.uuid,
          })) || [];
        return options;
      } catch (error) {
        console.error('Error fetching members:', error);
        return [];
      }
    },
  },
] as FilterConfiguration<Filters>[];

const initialFilters: Filters = {
  clinic: [],
  optOutStatus: [],
  icd10Codes: [],
  insuranceCompany: [],
  cptCodes: [],
  rpmStatus: [],
  memberSearch: [],
};

export const PatientSelection: React.FC<PatientSelectionProps> = ({ clinics, onAfterAction, currentProcess }) => {
  const defaultClinicIds = useMemo(() => clinics.map((c) => c.id), [clinics]);
  const [patientsData, setPatientsData] = useState<BillingMember[]>([]);
  const [pagination, setPagination] = useState<BillingPagination>(INITIAL_PAGINATION);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [icd10CodeOptions, setIcd10CodeOptions] = useState<SelectOption[]>([]);
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    isApplying: false,
    isClearing: false,
    isProcessing: false,
  });

  // Check if there's an active process that should disable the button
  const hasActiveProcess = currentProcess && isActiveStatus(currentProcess.status);
  const isButtonDisabled = loadingStates.isProcessing || !!hasActiveProcess;

  const [skipEffect, setSkipEffect] = useState(false);
  const [allRecordsSelected, setAllRecordsSelected] = useState(false);
  const [excludedRows, setExcludedRows] = useState<{ uuid: string; index: number }[]>([]);

  // Clear selected patients when switching processes
  useEffect(() => {
    setSelectedRows([]);
    setAllRecordsSelected(false);
    setExcludedRows([]);
  }, [currentProcess?.id]);

  const authProvider = useAuthProvider();
  const notify = useNotify();

  const clinicOptions: SelectOption[] = useMemo(
    () => clinics.map((clinic) => ({ label: clinic.name, value: `${clinic.id}` })),
    [clinics],
  );

  const getClinicIds = useCallback(
    (selectedClinicIds: string[]) => {
      const parsed = selectedClinicIds.map((id) => parseInt(id)).filter((id) => !isNaN(id));
      return parsed.length > 0 ? parsed : defaultClinicIds;
    },
    [defaultClinicIds],
  );

  const fetchIcd10CodeOptions = async () => {
    try {
      const icd10Codes = await healthDataService.findAllConditions({
        status: ConditionStatus.ACTIVE,
        pageSize: 100, // TODO: for now pagination is not needed, but we need to handle multiple pages soon
      });
      if (icd10Codes?.data) {
        setIcd10CodeOptions(
          icd10Codes.data.conditions.map(({ condition }) => ({
            value: condition.icd10Code,
            label: condition.icd10Code,
          })),
        );
      }
    } catch (error) {
      console.error('Error fetching available icd10 codes:', error);
    }
  };

  const fetchPatients = async (parameters: RpmPatientSelectionFilters) => {
    setLoadingStates((prev) => ({ ...prev, isApplying: true }));

    try {
      const response = await new BillingService().getPatients(parameters);
      setPatientsData(response?.data || []);
      setPagination(response?.pagination || INITIAL_PAGINATION);
    } catch (error) {
      console.error('Error fetching patients:', error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, isApplying: false }));
    }
  };

  const handlePageChange = (newPage: number) => {
    const parameters: RpmPatientSelectionFilters = {
      clinicIds: getClinicIds(filters.clinic),
      insuranceCompanyIds: filters.insuranceCompany || [],
      cptCodes: filters.cptCodes || [],
      icd10Codes: filters.icd10Codes ? filters.icd10Codes.map((code) => code.toUpperCase()) : [],
      rpmStatuses: filters.rpmStatus || [],
      optOutStatus: filters.optOutStatus || [],
      memberIds: filters.memberSearch || [],
      page: newPage,
      limit: pagination.limit,
    };
    fetchPatients(parameters);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    const parameters: RpmPatientSelectionFilters = {
      clinicIds: getClinicIds(filters.clinic),
      insuranceCompanyIds: filters.insuranceCompany || [],
      cptCodes: filters.cptCodes || [],
      icd10Codes: filters.icd10Codes ? filters.icd10Codes.map((code) => code.toUpperCase()) : [],
      rpmStatuses: filters.rpmStatus || [],
      optOutStatus: filters.optOutStatus || [],
      memberIds: filters.memberSearch || [],
      page: 1,
      limit: newLimit,
    };
    fetchPatients(parameters);
  };

  const updatedFilterConfigurations = useMemo(
    () =>
      filterConfigurations.map((config) => {
        switch (config.key) {
          case 'clinic':
            return { ...config, options: clinicOptions };
          case 'icd10Codes':
            return { ...config, options: icd10CodeOptions };
          default:
            return config;
        }
      }),
    [clinicOptions, icd10CodeOptions],
  );

  useEffect(() => {
    fetchIcd10CodeOptions();
  }, []);

  useEffect(() => {
    if (!skipEffect && defaultClinicIds.length > 0) {
      const parameters: RpmPatientSelectionFilters = {
        clinicIds: getClinicIds(filters.clinic),
        insuranceCompanyIds: filters.insuranceCompany || [],
        cptCodes: filters.cptCodes || [],
        icd10Codes: filters.icd10Codes ? filters.icd10Codes.map((code) => code.toUpperCase()) : [],
        rpmStatuses: filters.rpmStatus || [],
        optOutStatus: filters.optOutStatus || [],
        memberIds: filters.memberSearch || [],
        page: pagination.currentPage,
        limit: pagination.limit,
      };

      fetchPatients(parameters);
    }
  }, [filters, pagination.currentPage, pagination.limit, skipEffect, defaultClinicIds, getClinicIds]);

  const handleApplyFilters = async (newFilters: Filters) => {
    setLoadingStates((prev) => ({ ...prev, isApplying: true }));

    try {
      const parameters: RpmPatientSelectionFilters = {
        clinicIds: getClinicIds(newFilters.clinic),
        insuranceCompanyIds: newFilters.insuranceCompany || [],
        cptCodes: newFilters.cptCodes || [],
        icd10Codes: newFilters.icd10Codes
          ? filters.icd10Codes.map((code) => code.toUpperCase())
          : [],
        rpmStatuses: newFilters.rpmStatus || [],
        optOutStatus: newFilters.optOutStatus || [],
        memberIds: newFilters.memberSearch || [],
        page: 1,
        limit: pagination.limit,
      };

      await fetchPatients(parameters);
      setFilters(newFilters);
      setSelectedRows([]);
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, isApplying: false }));
    }
  };

  const handleClearFilters = async () => {
    setLoadingStates((prev) => ({ ...prev, isClearing: true }));
    setSkipEffect(true);

    try {
      const parameters = {
        clinicIds: defaultClinicIds,
        insuranceCompaniesIds: [],
        cptCodes: [],
        icd10Codes: [],
        rpmStatuses: [],
        optOutStatus: [],
        memberIds: [],
        page: 1,
        limit: pagination.limit,
      };
      await fetchPatients(parameters);
      setFilters(initialFilters);
      setSelectedRows([]);
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, isClearing: false }));
      setSkipEffect(false);
    }
  };

  const handleProcessFilters = useCallback(async () => {
    if (selectedRows.length === 0) {
      notify('Please select at least one row before proceeding or not valid rows found');
      return;
    }

    setLoadingStates((prev) => ({ ...prev, isProcessing: true }));

    const identity = (await authProvider.getIdentity!()) as Identity;

    if (!identity?.uuid || !identity?.fullName) {
      notify(INVALID_STORAGE_ERROR, 'error');
      setLoadingStates((prev) => ({ ...prev, isProcessing: false }));
      return;
    }

    try {
      const billingService = new BillingService();
      let select: string[] | null = null;
      let exclude: string[] | null = null;

      if (allRecordsSelected && excludedRows.length) {
        exclude = excludedRows.map(({ uuid }) => uuid);
      } else if (!allRecordsSelected && selectedRows.length) {
        const patientsMap = new Map(patientsData.map((patient) => [patient.id, patient]));
        select = selectedRows
          .map((id) => patientsMap.get(id)?.memberUuid)
          .filter((uuid): uuid is string => uuid !== undefined);
      }

      const response = await billingService.generateClaimsBulk(
        {
          ...(select && { select }),
          ...(exclude && { exclude }),
        },
        {
          ...(filters.clinic?.length && {
            clinicIds: filters.clinic.map((clinicId) => Number.parseInt(clinicId)),
          }),
          ...(filters.icd10Codes?.length && { icd10Codes: filters.icd10Codes }),
          ...(filters.insuranceCompany?.length && {
            insuranceCompanyIds: filters.insuranceCompany,
          }),
          ...(filters.memberSearch?.length && { memberUuids: filters.memberSearch }),
          ...(filters.optOutStatus?.length && { optOutStatus: filters.optOutStatus }),
          ...(filters.rpmStatus?.length && { rpmStatuses: filters.rpmStatus }),
          ...(filters.cptCodes?.length && { cptCodes: filters.cptCodes }),
        },
        { uuid: identity.uuid, fullName: identity.fullName },
        { runInBackground: true }, // Enable async processing
      );

      if (response) {
        const processId = billingService.getProcessId(response);
        onAfterAction(processId);
        
        // Show different notifications based on response type
        if (billingService.isBackgroundResponse(response)) {
          notify('Claims creation started in the background. You will see updates in real-time.', 'info');
        } else {
          const summary = billingService.createProcessSummary(response);
          notify(`Claims creation completed. ${summary}`, 'success');
        }
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      console.error('Error processing filters:', axiosError);

      let message = 'An unexpected error occurred while generating the claims.';

      if (axiosError.code === 'ECONNABORTED') {
        message = 'The request timed out. Please, reload the page.';
      } else if (axiosError.response?.data?.message) {
        message = axiosError.response.data.message;
      } else if (axiosError.message) {
        message = axiosError.message;
      }

      notify(message, { type: 'error' });
    } finally {
      setLoadingStates((prev) => ({ ...prev, isProcessing: false }));
    }
  }, [
    selectedRows,
    patientsData,
    onAfterAction,
    allRecordsSelected,
    filters,
    excludedRows,
    authProvider.getIdentity,
    notify,
  ]);

  return (
    <Box>
      <RPMFilters<Filters>
        configurations={updatedFilterConfigurations}
        onApplyFilters={handleApplyFilters}
        onProcessFilters={handleProcessFilters}
        onClearFilters={handleClearFilters}
        onProcessLabel={
          hasActiveProcess
            ? `Creating Claims...`
            : allRecordsSelected
            ? `Calculate all selected patients`
            : `Calculate ${selectedRows.length} patients`
        }
        initialFilters={initialFilters}
        selectedCount={selectedRows.length}
        loadingStates={{
          ...loadingStates,
          isProcessing: isButtonDisabled
        }}
        filters={filters}
        allRecordsSelection={allRecordsSelected}
      />

      <PatientSelectionTable
        allRecordsSelected={allRecordsSelected}
        data={patientsData}
        excludedRows={excludedRows}
        isLoading={loadingStates.isApplying}
        onAllRecordsSelected={setAllRecordsSelected}
        onPageChange={handlePageChange}
        onRowExclusionChange={setExcludedRows}
        onRowSelectionChange={setSelectedRows}
        onRowsPerPageChange={handleRowsPerPageChange}
        pagination={pagination}
        selectedRows={selectedRows}
      />
    </Box>
  );
};
